import { useState } from 'react';
import { ApiError, api } from '../api';
import type { ClaimKeyIn, TeamSummary, UnmanagedKeyOut } from '../types';

interface ClaimKeyFormProps {
  unmanagedKey: UnmanagedKeyOut;
  teams: TeamSummary[];
  onSubmit: () => Promise<void>;
  onCancel: () => void;
}

export function ClaimKeyForm({ unmanagedKey, teams, onSubmit, onCancel }: ClaimKeyFormProps) {
  const [teamId, setTeamId] = useState(unmanagedKey.suggested_team_id ?? '');
  const [ownerEmail, setOwnerEmail] = useState('');
  const [confirming, setConfirming] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    // First phase: require confirmation before submission
    if (!confirming) {
      if (!teamId) {
        setError('Please select a team');
        return;
      }
      if (!ownerEmail.trim()) {
        setError('Owner email is required');
        return;
      }
      setConfirming(true);
      return;
    }

    // Second phase: confirmed, submit
    setSubmitting(true);
    try {
      const data: ClaimKeyIn = {
        team_id: teamId,
        owner_email: ownerEmail.trim(),
        label: unmanagedKey.label || null,
      };
      await api.claimUnmanagedKey(unmanagedKey.id, data);
      await onSubmit();
    } catch (err) {
      setConfirming(false);
      if (err instanceof ApiError) {
        if (err.code === 'already_claimed') {
          setError('This key was already claimed. Refresh to see the update.');
        } else if (err.code === 'unmanaged_key_absent_upstream') {
          setError('This key is no longer in OpenRouter. It may have been deleted.');
        } else if (err.code === 'unmanaged_key_not_found') {
          setError('This key could not be found in the database.');
        } else if (err.code === 'team_not_found') {
          setError('The selected team does not exist.');
        } else {
          setError(`Error: ${err.message}`);
        }
      } else {
        setError(err instanceof Error ? err.message : String(err));
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = () => {
    if (confirming) {
      setConfirming(false);
      setError(null);
    } else {
      onCancel();
    }
  };

  if (confirming) {
    return (
      <div className="form-actions" style={{ flexDirection: 'column', gap: '8px' }}>
        <div className="form-group" style={{ margin: 0 }}>
          <div className="callout callout-warning" style={{ margin: 0 }}>
            <strong>Confirm claim:</strong>
            <ul style={{ margin: '6px 0 0', paddingLeft: '20px', fontSize: '13px' }}>
              <li>Team: <strong>{teams.find((t) => t.id === teamId)?.name ?? 'unknown'}</strong></li>
              <li>Owner: <strong>{ownerEmail}</strong></li>
            </ul>
          </div>
        </div>
        {error && <div className="form-error">{error}</div>}
        <button
          onClick={handleSubmit}
          className="primary"
          disabled={submitting}
          style={{ width: '100%' }}
        >
          {submitting ? 'Claiming...' : 'Confirm and claim'}
        </button>
        <button onClick={handleCancel} disabled={submitting} style={{ width: '100%' }}>
          Cancel
        </button>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="key-action-form">
      <div className="form-group" style={{ margin: '0 0 8px' }}>
        <label htmlFor={`team-${unmanagedKey.id}`} style={{ fontSize: '12px' }}>
          Team <span className="required">*</span>
        </label>
        <select
          id={`team-${unmanagedKey.id}`}
          value={teamId}
          onChange={(e) => setTeamId(e.target.value)}
          disabled={submitting}
          required
          style={{ fontSize: '12px', padding: '4px 6px' }}
        >
          <option value="">Select a team...</option>
          {teams.map((t) => (
            <option key={t.id} value={t.id}>
              {t.name}
            </option>
          ))}
        </select>
      </div>

      <div className="form-group" style={{ margin: '0 0 8px' }}>
        <label htmlFor={`owner-${unmanagedKey.id}`} style={{ fontSize: '12px' }}>
          Owner email <span className="required">*</span>
        </label>
        <input
          type="text"
          id={`owner-${unmanagedKey.id}`}
          value={ownerEmail}
          onChange={(e) => setOwnerEmail(e.target.value)}
          placeholder="user@example.com"
          disabled={submitting}
          required
          style={{ fontSize: '12px', padding: '4px 6px', maxWidth: 'none' }}
        />
      </div>

      {error && <div className="action-error">{error}</div>}

      <div className="form-actions" style={{ margin: '8px 0 0', gap: '4px' }}>
        <button type="submit" className="primary" disabled={submitting} style={{ fontSize: '12px' }}>
          {confirming ? 'Confirm' : 'Review'}
        </button>
        <button type="button" onClick={handleCancel} disabled={submitting} style={{ fontSize: '12px' }}>
          Cancel
        </button>
      </div>
    </form>
  );
}
